import React, {memo, ReactElement} from 'react';

import cs from 'classnames';
import {colors} from 'js-libs/sweetcommons/styles';
import {sortBy} from 'lodash';
import styled from 'styled-components';

import {LazyTooltip} from 'app/components/general/LazyTooltip';

import type {BaseItemPickerObject} from './SimpleItemPickerList';

const StyledLabel = styled.label`
  &.disabled {
    color: ${colors.light};
  }
`;

type Props<NamedItemType> = {
  item: NamedItemType;
  isSelected: boolean;
  disabledText?: string;
  showDescription: boolean;
  getFallback: (item: BaseItemPickerObject) => ReactElement<'span'> | string;
  setSelected: (
    selected:
      | Array<NamedItemType>
      | ((selected: Array<NamedItemType>) => Array<NamedItemType>)
  ) => void;
};

export const SimpleItemPickerItem = memo<Props<BaseItemPickerObject>>(
  <NamedItemType extends BaseItemPickerObject>({
    isSelected,
    setSelected,
    disabledText,
    item,
    showDescription,
    getFallback,
  }: Props<NamedItemType>) => {
    const disabled = disabledText != null;

    const canRenderDescription =
      showDescription &&
      item.content_type === 'team' &&
      item.description !== '';

    const addItem = (item: NamedItemType) => {
      setSelected((selected: Array<NamedItemType>) =>
        sortBy([...selected, item], ['url'])
      );
    };

    const removeItem = (item: NamedItemType) => {
      setSelected((selected: Array<NamedItemType>) =>
        selected.filter((i) => i.url != item.url)
      );
    };

    return (
      <LazyTooltip placement="top" tooltip={disabledText} hide={!disabled}>
        <li className={isSelected === true ? 'selected' : ''}>
          <StyledLabel className={`checkbox ${disabled ? 'disabled' : ''}`}>
            <input
              type="checkbox"
              className={cs('mr-2', {
                'mt-2': canRenderDescription,
              })}
              disabled={disabled}
              checked={isSelected}
              onChange={() => (isSelected ? removeItem(item) : addItem(item))}
            />{' '}
            <div>
              <div>{getFallback(item)}</div>
              {canRenderDescription ? (
                <div className="description small text-secondary">
                  {item.description}
                </div>
              ) : null}
            </div>
          </StyledLabel>
        </li>
      </LazyTooltip>
    );
  },
  (
    prevProps: Props<BaseItemPickerObject>,
    nextProps: Props<BaseItemPickerObject>
  ) =>
    prevProps.item.url === nextProps.item.url &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.disabledText === nextProps.disabledText
);
